import { useState, useEffect, useCallback } from "react";
import { 
  Search, Filter, FileText, Eye, CheckCircle2, XCircle, 
  Clock, AlertTriangle, ChevronDown, Download, Send,
  Calendar, Car, Building2, Package, Euro, Plus, Trash2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RicambistaRichieste({ user }) {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOfficina, setFilterOfficina] = useState("all");
  const [filterUrgency, setFilterUrgency] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  
  const [officine, setOfficine] = useState([]);
  
  // Quote form state
  const [quoteItems, setQuoteItems] = useState([]);
  const [quoteNotes, setQuoteNotes] = useState("");
  const [deliveryEstimate, setDeliveryEstimate] = useState("");
  const [validityDays, setValidityDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [quoteUrgency, setQuoteUrgency] = useState("standard");

  const fetchData = useCallback(async () => {
    try {
      const [reqRes, offRes] = await Promise.all([
        authFetch(`${API}/ricambista/requests`, { }),
        authFetch(`${API}/ricambista/officine`, { })
      ]);
      
      if (reqRes.ok) {
        const data = await reqRes.json();
        setRequests(data);
        setFilteredRequests(data);
      }
      if (offRes.ok) setOfficine(await offRes.json());
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = [...requests];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.vehicle_plate?.toLowerCase().includes(term) ||
        r.description?.toLowerCase().includes(term) ||
        r.officina_name?.toLowerCase().includes(term) ||
        r.items?.some(i => i.description?.toLowerCase().includes(term) || i.code?.toLowerCase().includes(term))
      );
    }
    
    if (filterStatus !== "all") {
      filtered = filtered.filter(r => r.status === filterStatus);
    }
    
    if (filterOfficina !== "all") {
      filtered = filtered.filter(r => r.officina_id === filterOfficina);
    }
    
    if (filterUrgency !== "all") {
      filtered = filtered.filter(r => r.urgency === filterUrgency);
    }
    
    if (filterDateFrom) {
      filtered = filtered.filter(r => new Date(r.created_at) >= new Date(filterDateFrom));
    }
    if (filterDateTo) {
      filtered = filtered.filter(r => new Date(r.created_at) <= new Date(filterDateTo));
    }
    
    setFilteredRequests(filtered);
  }, [requests, searchTerm, filterStatus, filterOfficina, filterUrgency, filterDateFrom, filterDateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);


  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      const response = await authFetch(`${API}/ricambista/requests/${requestId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Stato aggiornato a ${newStatus}`);
        fetchData();
        setShowDetailDialog(false);
      } else {
        toast.error("Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const openQuoteDialog = (request) => {
    setSelectedRequest(request);
    // Initialize quote items from request items
    // Filtra le righe di manodopera - mostra solo ricambi/articoli
    const filteredItems = (request.items || request.parts || []).filter(item => 
      !item.is_labor && item.line_type !== "MANODOPERA" && !item.description?.startsWith("Manodopera:")
    );
    
    const items = filteredItems.map(item => ({
      description: item.description || "",
      code: item.code || "",
      brand: "",
      quantity: item.quantity || 1,
      unit_price: "",
      availability: "disponibile",
      delivery_days: null,
      notes: ""
    }));
    setQuoteItems(items.length > 0 ? items : [{
      description: "",
      code: "",
      brand: "",
      quantity: 1,
      unit_price: "",
      availability: "disponibile",
      delivery_days: null,
      notes: ""
    }]);
    setQuoteNotes("");
    setDeliveryEstimate("");
    setValidityDays(7);
    setQuoteUrgency(request.urgency || "standard");
    setShowQuoteDialog(true);
  };

  const updateQuoteItem = (index, field, value) => {
    const updated = [...quoteItems];
    updated[index] = { ...updated[index], [field]: value };
    setQuoteItems(updated);
  };

  const addQuoteItem = () => {
    setQuoteItems([...quoteItems, {
      description: "",
      code: "",
      brand: "",
      quantity: 1,
      unit_price: "",
      availability: "disponibile",
      delivery_days: null,
      notes: ""
    }]);
  };

  const removeQuoteItem = (index) => {
    if (quoteItems.length > 1) {
      setQuoteItems(quoteItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotal = () => {
    return quoteItems.reduce((sum, item) => {
      return sum + (parseFloat(item.unit_price) || 0) * (parseInt(item.quantity) || 1);
    }, 0);
  };

  const handleSendQuote = async () => {
    // Validate
    const hasValidItems = quoteItems.length > 0;
    if (!hasValidItems) {
      toast.error("Inserisci almeno un articolo con prezzo");
      return;
    }

    setSubmitting(true);
    try {
      const response = await authFetch(`${API}/ricambista/requests/${selectedRequest.id}/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: quoteItems,
          notes: quoteNotes,
          delivery_estimate: deliveryEstimate,
          validity_days: validityDays,
          urgency: quoteUrgency
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Preventivo inviato! Totale: €${data.total.toFixed(2)}`);
        setShowQuoteDialog(false);
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Errore nell'invio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-zinc-500/20 text-zinc-400",
      sent: "bg-blue-500/20 text-blue-400",
      viewed: "bg-cyan-500/20 text-cyan-400",
      pending: "bg-yellow-500/20 text-yellow-400",
      in_lavorazione: "bg-blue-500/20 text-blue-400",
      quotato: "bg-purple-500/20 text-purple-400",
      confermato: "bg-green-500/20 text-green-400",
      rifiutato: "bg-red-500/20 text-red-400",
      completato: "bg-zinc-500/20 text-zinc-400"
    };
    
    const labels = {
      draft: "Bozza",
      sent: "Inviata",
      viewed: "Visualizzata",
      pending: "In Attesa",
      in_lavorazione: "In Lavorazione",
      quotato: "Quotato",
      confermato: "Confermato",
      rifiutato: "Rifiutato",
      completato: "Completato"
    };
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getUrgencyBadge = (urgency) => {
    const urgencyStyles = {
      standard: null,
      morning: "bg-yellow-500/20 text-yellow-400",
      evening: "bg-amber-500/20 text-amber-400",
      today: "bg-orange-500/20 text-orange-400",
      vehicle_down: "bg-red-500/20 text-red-400"
    };
    
    const urgencyLabels = {
      standard: null,
      morning: "Mattinata",
      evening: "Serata",
      today: "Oggi",
      vehicle_down: "FERMO"
    };
    
    if (!urgencyLabels[urgency]) return null;
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${urgencyStyles[urgency]}`}>
        {urgencyLabels[urgency]}
      </span>
    );
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setFilterOfficina("all");
    setFilterUrgency("all");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Richieste Preventivo</h1>
          <p className="text-zinc-400 text-sm mt-1">
            Gestisci le richieste dalle officine collegate
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-400">
            {filteredRequests.length} di {requests.length} richieste
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-zinc-400" />
          <span className="text-sm text-zinc-400">Filtri</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Cerca targa, articolo, descrizione..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger>
              <SelectValue placeholder="Tutti gli stati" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="draft">Bozza</SelectItem>
              <SelectItem value="sent">Inviata</SelectItem>
              <SelectItem value="in_lavorazione">In Lavorazione</SelectItem>
              <SelectItem value="quotato">Quotato</SelectItem>
              <SelectItem value="confermato">Confermato</SelectItem>
              <SelectItem value="rifiutato">Rifiutato</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={filterOfficina} onValueChange={setFilterOfficina}>
            <SelectTrigger>
              <SelectValue placeholder="Tutte le officine" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le officine</SelectItem>
              {officine.map(off => (
                <SelectItem key={off.user_id} value={off.user_id}>
                  {off.company_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterUrgency} onValueChange={setFilterUrgency}>
            <SelectTrigger>
              <SelectValue placeholder="Tutte" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le urgenze</SelectItem>
              <SelectItem value="standard">Standard</SelectItem>
              <SelectItem value="today">Oggi</SelectItem>
              <SelectItem value="vehicle_down">Fermo Macchina</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={clearFilters}>
            Pulisci filtri
          </Button>
        </div>
        
        {/* Date range */}
        <div className="flex items-center gap-4 mt-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-zinc-500" />
            <span className="text-sm text-zinc-400">Da:</span>
            <Input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="w-40"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">A:</span>
            <Input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="w-40"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-3 text-xs text-zinc-400 font-medium">Data</th>
                <th className="text-left p-3 text-xs text-zinc-400 font-medium">Officina</th>
                <th className="text-left p-3 text-xs text-zinc-400 font-medium">Veicolo</th>
                <th className="text-left p-3 text-xs text-zinc-400 font-medium">Descrizione</th>
                <th className="text-center p-3 text-xs text-zinc-400 font-medium">Priorità</th>
                <th className="text-center p-3 text-xs text-zinc-400 font-medium">Stato</th>
                <th className="text-center p-3 text-xs text-zinc-400 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-zinc-500">
                    Nessuna richiesta trovata
                  </td>
                </tr>
              ) : (
                filteredRequests.map((req) => (
                  <tr key={req.id} className="hover:bg-zinc-800/50 transition-colors">
                    <td className="p-3 text-sm text-zinc-400">
                      {new Date(req.created_at).toLocaleDateString('it-IT')}
                    </td>
                    <td className="p-3">
                      <p className="text-sm text-white">{req.officina_name}</p>
                      <p className="text-xs text-zinc-500">{req.officina_city}</p>
                    </td>
                    <td className="p-3">
                      <p className="text-sm font-mono text-white">{req.vehicle_plate}</p>
                      <p className="text-xs text-zinc-500">{req.vehicle_brand} {req.vehicle_model}</p>
                    </td>
                    <td className="p-3">
                      <p className="text-sm text-white truncate max-w-xs">{req.description || req.notes}</p>
                      <p className="text-xs text-zinc-500">{req.items?.length || 0} articoli</p>
                    </td>
                    <td className="p-3 text-center">
                      {getUrgencyBadge(req.urgency)}
                    </td>
                    <td className="p-3 text-center">
                      {getStatusBadge(req.status)}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedRequest(req);
                            setShowDetailDialog(true);
                          }}
                          title="Visualizza"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {(req.status === 'sent' || req.status === 'draft' || req.status === 'viewed' || req.status === 'in_lavorazione') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openQuoteDialog(req)}
                            className="text-primary hover:text-primary"
                            title="Invia Preventivo"
                          >
                            <Euro className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettaglio Richiesta</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-6 mt-4">
              {/* Request Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-zinc-800/50">
                  <p className="text-xs text-zinc-400">Officina</p>
                  <p className="text-white font-medium">{selectedRequest.officina_name}</p>
                  <p className="text-xs text-zinc-500">{selectedRequest.officina_city}</p>
                </div>
                <div className="p-3 rounded-lg bg-zinc-800/50">
                  <p className="text-xs text-zinc-400">Data Richiesta</p>
                  <p className="text-white">{new Date(selectedRequest.created_at).toLocaleString('it-IT')}</p>
                </div>
              </div>
              
              {/* Vehicle Info */}
              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h4 className="text-sm font-semibold text-zinc-400 mb-2 flex items-center gap-2">
                  <Car className="w-4 h-4" /> Veicolo
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-zinc-500">Targa</p>
                    <p className="text-white font-mono">{selectedRequest.vehicle_plate}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Marca/Modello</p>
                    <p className="text-white">{selectedRequest.vehicle_brand} {selectedRequest.vehicle_model}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Km</p>
                    <p className="text-white">{selectedRequest.vehicle_km?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              
              {/* Items */}
              <div>
                <h4 className="text-sm font-semibold text-zinc-400 mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4" /> Articoli Richiesti
                </h4>
                <div className="space-y-2">
                  {(selectedRequest.items || selectedRequest.parts || []).map((item, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-zinc-800/50 flex items-center justify-between">
                      <div>
                        <p className="text-white">{item.description}</p>
                        <p className="text-xs text-zinc-500">Codice: {item.code || 'N/A'} • Qta: {item.quantity}</p>
                      </div>
                    </div>
                  ))}
                  {(!(selectedRequest.items?.length) && !(selectedRequest.parts?.length)) && (
                    <p className="text-zinc-500 text-sm">Nessun articolo specificato</p>
                  )}
                </div>
              </div>
              
              {/* Notes */}
              {(selectedRequest.notes || selectedRequest.description) && (
                <div>
                  <h4 className="text-sm font-semibold text-zinc-400 mb-2">Note</h4>
                  <p className="text-white p-3 rounded-lg bg-zinc-800/50">
                    {selectedRequest.notes || selectedRequest.description}
                  </p>
                </div>
              )}

              {/* Ricambista Response (if quotato) */}
              {selectedRequest.ricambista_response && (
                <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                  <h4 className="text-sm font-semibold text-purple-400 mb-3">Preventivo Inviato</h4>
                  <div className="space-y-2">
                    {selectedRequest.ricambista_response.items?.map((item, idx) => {
                      const avail = item.availability || (item.available ? "disponibile" : "non_fornibile");
                      const availLabel = avail === "disponibile" ? "Disponibile" : avail === "ordinabile" ? "Ordinabile" : "Non fornibile";
                      const availColor = avail === "disponibile" ? "text-green-400" : avail === "ordinabile" ? "text-yellow-400" : "text-red-400";
                      return (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <span className="text-white">
                          {item.description} {item.brand && `(${item.brand})`} x{item.quantity}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs ${availColor}`}>{availLabel}</span>
                          <span className={avail !== "non_fornibile" ? "text-green-400" : "text-red-400"}>
                            {avail !== "non_fornibile" ? `€${item.total?.toFixed(2)}` : "-"}
                          </span>
                        </div>
                      </div>
                      );
                    })}
                    <div className="pt-2 border-t border-purple-500/30 flex justify-between">
                      <span className="text-purple-300 font-medium">Totale</span>
                      <span className="text-purple-400 font-bold">
                        €{selectedRequest.ricambista_response.total_amount?.toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-400 mt-2">
                      Valido fino: {new Date(selectedRequest.ricambista_response.valid_until).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Status & Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-400">Stato attuale:</span>
                  {getStatusBadge(selectedRequest.status)}
                  {getUrgencyBadge(selectedRequest.urgency)}
                </div>
                
                <div className="flex items-center gap-2">
                  {(selectedRequest.status === 'sent' || selectedRequest.status === 'draft' || selectedRequest.status === 'viewed') && (
                    <Button
                      variant="outline"
                      onClick={() => handleUpdateStatus(selectedRequest.id, 'in_lavorazione')}
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Prendi in Carico
                    </Button>
                  )}
                  {(selectedRequest.status === 'sent' || selectedRequest.status === 'draft' || selectedRequest.status === 'viewed' || selectedRequest.status === 'in_lavorazione') && (
                    <Button
                      onClick={() => {
                        setShowDetailDialog(false);
                        openQuoteDialog(selectedRequest);
                      }}
                    >
                      <Euro className="w-4 h-4 mr-2" />
                      Invia Preventivo
                    </Button>
                  )}
                  {selectedRequest.status !== 'rifiutato' && selectedRequest.status !== 'confermato' && (
                    <Button
                      variant="destructive"
                      onClick={() => handleUpdateStatus(selectedRequest.id, 'rifiutato')}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Rifiuta
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quote Dialog */}
      <Dialog open={showQuoteDialog} onOpenChange={setShowQuoteDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Euro className="w-5 h-5 text-primary" />
              Invia Preventivo
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-6 mt-4">
              {/* Request Summary */}
              <div className="p-4 rounded-lg bg-zinc-800/50">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white font-medium">{selectedRequest.officina_name}</p>
                    <p className="text-sm text-zinc-400">
                      {selectedRequest.vehicle_plate} • {selectedRequest.vehicle_brand} {selectedRequest.vehicle_model}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={quoteUrgency} onValueChange={setQuoteUrgency}>
                      <SelectTrigger className="w-48 bg-zinc-800">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="morning">Urgente - In Mattinata</SelectItem>
                        <SelectItem value="evening">Urgente - In Serata</SelectItem>
                        <SelectItem value="today">Urgente - Oggi</SelectItem>
                        <SelectItem value="vehicle_down">Fermo Macchina</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Quote Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-white">Articoli Preventivo</h4>
                  <Button variant="outline" size="sm" onClick={addQuoteItem}>
                    <Plus className="w-4 h-4 mr-1" /> Aggiungi
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {quoteItems.map((item, idx) => (
                    <div key={idx} className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                      <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-4">
                          <label className="text-xs text-zinc-400 mb-1 block">Descrizione *</label>
                          <Input
                            value={item.description}
                            onChange={(e) => updateQuoteItem(idx, 'description', e.target.value)}
                            placeholder="Es: Pastiglie freno ant."
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs text-zinc-400 mb-1 block">Codice</label>
                          <Input
                            value={item.code}
                            onChange={(e) => updateQuoteItem(idx, 'code', e.target.value)}
                            placeholder="BRK001"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs text-zinc-400 mb-1 block">Marca</label>
                          <Input
                            value={item.brand}
                            onChange={(e) => updateQuoteItem(idx, 'brand', e.target.value)}
                            placeholder="Brembo"
                          />
                        </div>
                        <div className="col-span-1">
                          <label className="text-xs text-zinc-400 mb-1 block">Qta</label>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateQuoteItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs text-zinc-400 mb-1 block">Prezzo Unit. €</label>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={item.unit_price}
                            onChange={(e) => {
                              // Allow empty, digits, dot or comma
                              const val = e.target.value.replace(',', '.');
                              if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                updateQuoteItem(idx, 'unit_price', val);
                              }
                            }}
                            placeholder="0.00"
                            disabled={false}
                          />
                        </div>
                        <div className="col-span-1 flex items-end justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeQuoteItem(idx)}
                            disabled={quoteItems.length === 1}
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-700">
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-zinc-400">Stato:</label>
                          <Select
                            value={item.availability}
                            onValueChange={(val) => updateQuoteItem(idx, 'availability', val)}
                          >
                            <SelectTrigger className="w-[160px] h-8 text-sm" data-testid={`availability-select-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="disponibile">
                                <span className="text-green-400">Disponibile</span>
                              </SelectItem>
                              <SelectItem value="ordinabile">
                                <span className="text-yellow-400">Ordinabile</span>
                              </SelectItem>
                              <SelectItem value="non_fornibile">
                                <span className="text-red-400">Non fornibile</span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="text-right">
                          <span className="text-zinc-400 text-sm">Totale: </span>
                          <span className={`font-mono font-bold ${item.availability !== 'non_fornibile' ? 'text-white' : 'text-zinc-500'}`}>
                            €{((parseFloat(item.unit_price) || 0) * (item.quantity || 1)).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-end">
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
                  <span className="text-zinc-400 mr-4">Totale Preventivo:</span>
                  <span className="text-2xl font-bold text-primary">€{calculateTotal().toFixed(2)}</span>
                </div>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Stima Consegna</label>
                  <Input
                    value={deliveryEstimate}
                    onChange={(e) => setDeliveryEstimate(e.target.value)}
                    placeholder="Es: 2-3 giorni lavorativi"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Validità (giorni)</label>
                  <Input
                    type="number"
                    min="1"
                    max="30"
                    value={validityDays}
                    onChange={(e) => setValidityDays(parseInt(e.target.value) || 7)}
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Note Aggiuntive</label>
                <textarea
                  className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                  rows={3}
                  value={quoteNotes}
                  onChange={(e) => setQuoteNotes(e.target.value)}
                  placeholder="Note per l'officina..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowQuoteDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSendQuote} disabled={submitting}>
              {submitting ? "Invio..." : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Invia Preventivo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
